<script setup lang="ts">
const { t } = useI18n();
const { getCollectibleById } = useCollectiblesController();
const route = useRoute();
const router = useRouter();
const appStateStore = useAppStateStore();
const merchant = appStateStore.currentMerchant;
const title = ref<string>("");
const imageUrl = ref<string>("");

useSeoMeta({
  title: () => title.value,
  description: `${t("main.marketplaceSEO.description.collectibleDetail")}`,
  ogType: "website",
  ogTitle: () => title.value,
  ogDescription: `${t("main.marketplaceSEO.description.collectibleDetail")}`,
  ogUrl: "https://gfal.market",
  ogImage: () => imageUrl.value,
  twitterCard: "summary_large_image",
  twitterSite: "@GFAL_Official",
  twitterTitle: () => title.value,
  twitterDescription: `${t(
    "main.marketplaceSEO.description.collectibleDetail"
  )}`,
  twitterImage: () => imageUrl.value,
});

const backUrl = computed(() => {
  if (route.query.stackSetId) {
    return {
      path: `/market/${merchant}/stack/${route.query.stackSetId}`,
    };
  } else {
    return {
      path: `/market/${appStateStore.currentMerchant}`,
    };
  }
});

const setSEO = (collectible: Collectible) => {
  if (collectible) {
    title.value = `${t(`${collectible.translationId}.title`)} ${t(
      `${collectible.translationId}.subtitle`
    )} #${collectible.number}`;
    imageUrl.value = getPortraitItemImage(collectible.imageId).replace(
      "webp",
      "png"
    );
  }
};

const { data: collectible, status } = await useAsyncData(
  `get${merchant}CollectibleById`,
  async () => {
    const id = route.params.id?.toString();
    if (!id) return {} as Collectible;
    const response = await getCollectibleById(id);
    return response;
  },
  {
    default() {
      return {} as Collectible;
    },
    lazy: true,
  }
);

watch(
  collectible,
  (newCollectible) => {
    if (newCollectible) setSEO(newCollectible);
  },
  { immediate: true }
);
</script>

<template>
  <NuxtLinkLocale
    class="flex items-center my-7 cursor-pointer text-neutral-300 hover:text-primary-500"
    :to="backUrl">
    <NuxtIcon name="mdi:chevron-left" class="h-5 w-5 shrink-0" />
    <p class="text-xl font-bold">{{ $t("main.storeDetail.backToList") }}</p>
  </NuxtLinkLocale>
  <div
    v-if="status === 'pending' || !appStateStore.areTidsLoaded"
    class="flex items-center justify-center">
    <LoaderCardDetail />
  </div>
  <CardCollectibleDetail
    v-else-if="status === 'success' && collectible"
    :collectible="collectible" />
  <CommonNotFoundItem v-else />
</template>
